import EditLocationAltIcon from '@mui/icons-material/EditLocationAlt'
import {
  AccordionDetails,
  Box,
  Button,
  FormControlLabel,
  Grid,
  Switch,
  Typography,
} from '@mui/material'
import React, { useCallback, useEffect, useState } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { useIntl } from 'react-intl'
import { useGetContactByEntityIdQuery } from '../../../app/redux-fetch/apiCoreQuery'
import { formatDate } from '../../../app/utils'
import StyledAccordion from '../../../components/Accordions/StyledAccordion'
import StyledAccordionSummary from '../../../components/Accordions/StyledAccordionSummary'
import ActionButton from '../../../components/Buttons/ActionButton'
import DefaultSpacingGrid from '../../../components/Containers/DefaultSpacingGrid'
import FormDatePickerInput from '../../../components/Forms/FormDatePickerInput'
import FormInput from '../../../components/Forms/FormInput'
import FormSelect from '../../../components/Forms/FormSelect'
import commonMessages from '../../../components/Messages/commonMessages'
import InfoDisplayItem from '../../../components/Typographies/InfoDisplayItem'
import { LabelItem } from '../../../components/Typographies/LabelItem'
import { useOrderFormContext } from '../context/OrderFormContext'
import { useLocationModal } from '../hooks/useLocationModal'
import FlowSection from './FlowSection'
import messages from './messages'
import { getAvailableLocations, getLocationAddress, getLocationRevision, mapContactsToOptions } from './utils'

interface DeliverToFormProps {
  index: number
  disabled?: boolean;
}

const DeliverToForm: React.FC<DeliverToFormProps> = ({ index, disabled = false }) => {
  const { control, setValue, watch } = useFormContext()
  const { formatMessage } = useIntl()
  const { variant } = useOrderFormContext()

  const [expanded, setExpanded] = useState(true)

  const handleToggleAccordion = () => {
    setExpanded((prev) => !prev)
  }

  const customerId = watch('customerId')
  const oldLocations = watch(`bookings.${index}.deliverTo.locationId`)

  const {
    openModal,
    ModalComponent,
    locationOptions,
    locations,
  } = useLocationModal({
    onSave: (id) => {
      setValue(`bookings.${index}.deliverTo.location`, id)
    },
    customerId,
    isSelectLocationType: true,
    filterFunction: (location) => {
      return (
        location.enabled &&
        location.locationTypeId !== 0 &&
        location.locationTypeId !== 1
      )
    },
    oldLocations: oldLocations ? [oldLocations] : [],
    disabled,
  })

  const locationId = watch(`bookings.${index}.deliverTo.locationId`)
  const embeddedLocation = watch(`bookings.${index}.deliverTo.location`)

  const getLocationName = useCallback((locationId: number) => {
    return (
      locationOptions.find((location) => location.value === locationId)?.label ??
      embeddedLocation?.name
    )
  }, [locationOptions, embeddedLocation])

  const { data: contacts } = useGetContactByEntityIdQuery(
    locationId,
    {
      skip: !locationId || disabled,
      refetchOnMountOrArgChange: true,
    }
  )

  const getContactOptions = useCallback(() => {
    return mapContactsToOptions(contacts)
  }, [contacts])

  const consignments = watch('bookings') || []

  const currentConsignment = consignments[index]

  const usedLocationIds: number[] = []

  if (currentConsignment?.shipFrom?.locationId) {
    usedLocationIds.push(currentConsignment.shipFrom.locationId)
  }

  if (currentConsignment?.deliverTo?.locationId) {
    usedLocationIds.push(currentConsignment.deliverTo.locationId)
  }
  const isEmptyContainerReturn = watch(`bookings.${index}.deliverTo.emptyContainerReturn`)

  const selectedLocation = locations.find(
    (l: any) => l.id === locations.find((loc: any) => loc.id === locationId)?.id
  )

  const modeOfTransit = watch(`bookings.${index}.deliverTo.transitDetails.modeOfTransit`);

  useEffect(() => {
    if (modeOfTransit > -1) return
    if (!isEmptyContainerReturn) return
    if (selectedLocation?.defaultTransportModeId) {
      setValue(
        `bookings.${index}.deliverTo.transitDetails.modeOfTransit`,
        selectedLocation?.defaultTransportModeId
      )
    } else {
      setValue(`bookings.${index}.deliverTo.transitDetails.modeOfTransit`, -1)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLocation, isEmptyContainerReturn])

  const contactId = watch(`bookings.${index}.deliverTo.contactId`);
  const specialInstructions = watch(`bookings.${index}.deliverTo.specialInstructions`);
  const clearingRequired = watch(`bookings.${index}.deliverTo.clearingRequired`);
  const fallbackAddress = getLocationAddress(locations, locationId);
  const address =
    fallbackAddress !== 'N/A' ? fallbackAddress : embeddedLocation?.address?.address1 ?? 'N/A';
  const getContactName = (contactId: number) => {
    const contact = (getContactOptions() || []).find((c: any) => c.value === contactId);
    if (contact) return contact.label;
    const embeddedContact = embeddedLocation?.contacts?.find((c: any) => c.id === contactId);
    if (embeddedContact) {
      return `${embeddedContact.contactType?.name} - ${embeddedContact.name}`;
    }
    return 'N/A';
  };

  return (
    <>
      {ModalComponent}
      <FlowSection hasBorder={!!isEmptyContainerReturn}>
        <StyledAccordion
          defaultExpanded
          sx={(theme: any) => ({ backgroundColor: theme.palette?.accordion?.parent?.bg, marginBottom: '16px' })}
          expanded={expanded}
          onChange={handleToggleAccordion}
          title={formatMessage(messages.deliverTo)}
        >
          <StyledAccordionSummary
            index={`deliverto-content-${index}`}
            expanded={expanded}
            title={formatMessage(messages.deliverTo)}
          >
            <Grid container>
              <Grid item xs={12} md={3} sx={{ display: 'flex', justifyContent: 'flex-start', mr: '-76px' }}>
                <LabelItem>{formatMessage(messages.deliverTo)}</LabelItem>
              </Grid>
              {!expanded && (
                <>
                  <Grid item xs={12} md={3} sx={{ display: 'flex', justifyContent: 'flex-start', mr: '44px' }}>
                    <InfoDisplayItem
                      label={formatMessage(messages.deliverTo)}
                      value={getLocationName(locationId) ?? 'N/A'}
                      isSmallScreen={false}
                      allowOverlap={true}
                    />
                  </Grid>
                  <Grid item xs={12} md={4} sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                    <InfoDisplayItem
                      label={formatMessage(messages.deliveryDate)}
                      value={formatDate(watch(`bookings.${index}.deliverTo.deliveryDate`))}
                      isSmallScreen={false}
                      allowOverlap={true}
                    />
                  </Grid>
                </>
              )}
            </Grid>
          </StyledAccordionSummary>
          <AccordionDetails>
            {variant === 'summary' ? (
              <DefaultSpacingGrid container sx={{ pb: '8px' }}>
                <Grid item xs={12} md={6} sx={{ mr: '-24px' }}>
                  <InfoDisplayItem
                    label={formatMessage(messages.location)}
                    value={getLocationName(locationId) ?? 'N/A'}
                    isSmallScreen={false}
                    allowOverlap={true}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <InfoDisplayItem
                    label={formatMessage(messages.requestedDate)}
                    value={formatDate(watch(`bookings.${index}.deliverTo.deliveryDate`))}
                    isSmallScreen={false}
                    allowOverlap={true}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <InfoDisplayItem
                    label={formatMessage(messages.contact)}
                    value={getContactName(contactId)}
                    isSmallScreen={false}
                    allowOverlap={true}
                  />
                </Grid>
                <Grid item xs={12} md={6} sx={{ mr: '-24px' }}>
                  <InfoDisplayItem
                    label={formatMessage(messages.address)}
                    value={address}
                    isSmallScreen={false}
                    allowOverlap={true}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <InfoDisplayItem
                    label={formatMessage(messages.specialInstructions)}
                    value={specialInstructions || 'N/A'}
                    isSmallScreen={false}
                    allowOverlap={true}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <InfoDisplayItem
                    label={formatMessage(messages.clearingRequired)}
                    value={clearingRequired ? 'Yes' : 'No'}
                    isSmallScreen={false}
                    allowOverlap={true}
                  />
                </Grid>
              </DefaultSpacingGrid>
            ) : (
              <DefaultSpacingGrid container>
                <Grid item xs={12} md={6}>
                  <FormSelect
                    name={`bookings.${index}.deliverTo.locationId`}
                    label={formatMessage(messages.location)}
                    options={getAvailableLocations(locationOptions, usedLocationIds)}
                    allOptions={locationOptions}
                    required={true}
                    onChange={(e) => {
                      setValue(`bookings.${index}.deliverTo.contactId`, null)
                      const selectedId = +e.target.value
                      const revision = getLocationRevision(locations, selectedId)
                      setValue(`bookings.${index}.deliverTo.locationRevision`, revision)
                      const selectedLocation = locations.find((l: any) => l.id === selectedId)
                      setValue(`bookings.${index}.deliverTo.location`, selectedLocation ?? undefined)
                    }}
                    actionLabel={
                      !disabled ? (
                        <ActionButton
                          onClick={() => {
                            openModal()
                          }}
                          label={formatMessage(commonMessages.addNew)}
                        />
                      ) : undefined
                    }
                    fullWidth
                    disabled={disabled}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <FormDatePickerInput
                    name={`bookings.${index}.deliverTo.deliveryDate`}
                    label={formatMessage(messages.requestedDate)}
                    required={true}
                    format='YYYY-MM-DD HH:mm'
                    disabled={disabled}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <FormSelect
                    name={`bookings.${index}.deliverTo.contactId`}
                    label={formatMessage(messages.contact)}
                    disableLabel={formatMessage(messages.noContactsAvailable)}
                    options={getContactOptions()}
                    actionLabel={
                      !disabled ? (
                        <ActionButton
                          onClick={() => {
                            setValue(`bookings.${index}.deliverTo.contactId`, null)
                          }}
                          label={formatMessage(messages.clearSelection)}
                        />
                      ) : undefined
                    }
                    fullWidth
                    disabled={disabled}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box display='flex' alignItems='center' justifyContent='space-between'>
                    <Typography variant='subtitle1' gutterBottom sx={{ color: '#636363' }}>
                      {formatMessage(messages.address)}
                    </Typography>
                    {locationId && (
                      <Button
                        onClick={() => openModal(selectedLocation)}
                        size='small'
                        variant='text'
                        sx={{
                          textTransform: 'none',
                          minWidth: 'auto',
                          ml: 1,
                          color: '#3D7FDA',
                          fontWeight: 'bold',
                          '&:hover': {
                            backgroundColor: 'transparent',
                            textDecoration: 'underline',
                          },
                        }}
                        endIcon={<EditLocationAltIcon fontSize='small' sx={{ color: 'inherit' }} />}
                        disabled={disabled}
                      >
                        {formatMessage(commonMessages.editBtn)}
                      </Button>
                    )}
                  </Box>
                  <Typography variant='body2' sx={{ color: '#636363' }}>
                    {getLocationAddress(locations, locationId)}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormInput
                    name={`bookings.${index}.deliverTo.specialInstructions`}
                    label={formatMessage(messages.specialInstructions)}
                    placeholder={formatMessage(commonMessages.pleaseEnter)}
                    fullWidth
                    disabled={disabled}
                    maxLength={200}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box sx={{ pb: '16px' }}>
                    <Controller
                      name={`bookings.${index}.deliverTo.clearingRequired`}
                      control={control}
                      defaultValue={false}
                      render={({ field }) => (
                        <FormControlLabel
                          control={<Switch {...field} checked={field.value} disabled={disabled} />}
                          label={formatMessage(messages.clearingRequired)}
                        />
                      )}
                    />
                  </Box>
                </Grid>
              </DefaultSpacingGrid>
            )}
          </AccordionDetails>
        </StyledAccordion>
      </FlowSection>
    </>
  )
}

export default DeliverToForm
